/* eslint-disable no-param-reassign */
import Vue from 'vue'
import { $_bridge } from '@/nms/plugins/bridge.js'
import moment from 'moment-timezone'

// Типы для пользователя
interface User {
    id: string
    type_auth: string
    name: string
    password: string
    role?: 'administrator' | 'manager' | 'operator' | 'inform_secur_user'
    blocked?: boolean
    [key: string]: any
}

// Тип для пропсов пользователя
interface UserProp {
  name: string
  value: any
  [key: string]: any
}

// Тип для сохраненных данных
interface SavedState {
  module: string | null
  page: string | null
  userProps: {
    mapCenter: [number, number]
    sessionStart: string | null
    userPosition?: UserProp
    [key: string]: any
  }
}

// Тип для состояния модуля
interface AuthState {
  logged: boolean
  user: User | null
  loader: Record<string, boolean>
  editMode: boolean
  saved: SavedState
  users: any[]
  lock: boolean
}

// Функция получения начального состояния с типами
const getDefaultState = (): AuthState => {
  return {
    logged: false,
    user: null,
    loader: {},
    editMode: false,
    saved: {
      module: null,
      page: null,
      userProps: {
        mapCenter: [55.755919, 37.617589],
        sessionStart: null
      }
    },
    users: [],
    lock: false
  }
}

const state: AuthState = getDefaultState()

export default {
  state,

  mutations: {
    RESET_AUTH_STATE(state: AuthState) {
      const defaultState = getDefaultState()
      Object.assign(state, defaultState)
    },

    SET(state: AuthState, value: boolean) {
      state.logged = value
    },

    SET_USER(state: AuthState, value: User | null) {
      state.user = value
    },

    SET_LOADER(state: AuthState, data?: { key: string; value: boolean }) {
      if(data) {
        Vue.set(state.loader, data.key, data.value)
      } else {
        Vue.set(state, 'loader', {})
      }
    },

    SET_EDIT_MODE(state: AuthState, data?: boolean) {
      if(data !== undefined) {
        Vue.set(state, 'editMode', data)
      } else {
        Vue.set(state, 'editMode', false)
      }
    },

    SAVE_MODULE(state: AuthState, value: string | null) {
      state.saved.module = value
    },

    SAVE_PAGE(state: AuthState, value: string | null) {
      state.saved.page = value
    },

    SAVE_USER_PROPS(state: AuthState, props: AuthState['saved']['userProps']) {
      Vue.set(state.saved, 'userProps', props)
    },

    RESET_USER_PROPS(state: AuthState, data: AuthState['saved']['userProps']) {
      Vue.set(state.saved, 'userProps', data)
    },

    SAVE_USER_PROP(state: AuthState, prop: UserProp) {
      Vue.set(state.saved.userProps, prop.name, prop)
    },
  },

  actions: {
    async resetAuthState({ commit }: any) {
      commit('RESET_AUTH_STATE')
    },
    
    async checkForLogged({ commit }: any) {
      const logged = await $_bridge.isLogged()
      console.log("checkForLogged >>>> logged", logged)
      commit('SET', logged)
    },

    setLogged({ commit, dispatch }: any, logged: boolean) {
      commit('SET', logged)
      if(!logged) {
        dispatch('reset')
      }
    },

    setLoggedUser({ commit }: any, user: User) {
      console.log("logged user", user)
      commit('SET_USER', user)
      return user
    },

    async login({ commit }: any, { user, password, AD }: { user: string; password: string; AD?: any }) {
      const l = await $_bridge.connect({ user, password, AD })
      commit('SET', true)
      commit('SET_USER', user)
      return l
    },

    async KCLogin({ commit }: any, { code, user }: { code: string; user?: string }) {
      console.log('KCLogin', code)
      const l = await $_bridge.connect({ code: code })
      commit('SET', true)
      return l
    },

    async logout({ commit }: any) {
      await $_bridge.logout()
      commit('SET', false)
      commit('SET_USER', null)
      localStorage.removeItem('savedModule')
      localStorage.removeItem('savedPage')
    },

    setLoader({ commit }: any, data?: { key: string; value: boolean }) {
      if(data) {
        commit('SET_LOADER', data)
      } else {
        commit('SET_LOADER')
      }
    },

    setEditMode({ commit }: any, data?: boolean) {
      if(data !== undefined) {
        commit('SET_EDIT_MODE', data)
      } else {
        commit('SET_EDIT_MODE')
      }
    },

    saveModule({ commit }: any, path: string | null) {
      commit('SAVE_MODULE', path)
      if(path) {
        localStorage.setItem('savedModule', path)
      } else {
        localStorage.removeItem('savedModule')
      }
    },

    savePage({ commit }: any, path: string | null) {
      commit('SAVE_PAGE', path)
      if(path) {
        localStorage.setItem('savedPage', path)
      } else {
        localStorage.removeItem('savedPage')
      }
    },

    async saveUserProps({ commit, state }: any) {
      try {
        if(!state.user?.login) return
        await $_bridge.createPath('/config/userProps/' + state.user.login)
        const c$ = await $_bridge.getByPath('/config/userProps/' + state.user.login)
        const docs$ = await c$.documents(true)     
        if (!docs$) {
          await c$.attacheDocument('configure.json', JSON.stringify({}))
        } else {
          const configuration$ = docs$.find((doc$: any) => doc$.name === 'configure.json')
          if(!configuration$) {
            await c$.attacheDocument('configure.json', JSON.stringify({}))
            commit('SAVE_USER_PROPS', {})
          } else {
            const configuration = JSON.parse(configuration$.content)
            if(configuration) {
              commit('SAVE_USER_PROPS', configuration)
            }
          }
        }
      } catch(error) {
        console.error('STORE', 'AUTH', 'saveUserProps', 'smth wrong', error)
      }     
    },

    async saveUserProp({ commit, state }: any, prop: UserProp) {
      if(!prop) return
      try {
        if(!state.user?.login) return
        await $_bridge.createPath('/config/userProps/' + state.user.login)
        const c$ = await $_bridge.getByPath('/config/userProps/' + state.user.login)
        const docs$ = await c$.documents(true)
        if (!docs$) {
          await c$.attacheDocument('configure.json', JSON.stringify({ [prop.name]: prop }))
          commit('SAVE_USER_PROP', prop)
        } else {
          const configuration$ = docs$.find((doc$: any) => doc$.name === 'configure.json')
          if(!configuration$) {
            await c$.attacheDocument('configure.json', JSON.stringify({ [prop.name]: prop }))
            commit('SAVE_USER_PROP', prop)
          } else {
            try {
              const configuration = JSON.parse(configuration$.content)
              if(configuration) {
                configuration[prop.name] = prop
                const updated$ = await $_bridge.updateDocument(configuration$.id, {
                  content: JSON.stringify(configuration)
                })
                if(updated$) {
                  commit('SAVE_USER_PROP', prop)
                }
              }
            } catch(err) {
              console.log("parse err", err)
              await c$.attacheDocument('configure.json', JSON.stringify({ [prop.name]: prop }))
              commit('SAVE_USER_PROP', prop)  
            }    
          }
        }
      } catch(error) {
        console.error('STORE', 'AUTH', 'saveUserProp', 'smth wrong', error)
      }      
    },

    async updateObject({ commit, state }: any, data: { $id?: string; [key: string]: any }) {
      try {
        if(!data.$id) return
        const query = {
          id: [data.$id],
          notNull: true,
        }
        const data$ = {
          last_update_user: state.user?.login,
          last_update_date: moment()
        }
        const obj = await $_bridge.cmdbQuery(query)
        if(obj && obj[0]) {
          const updatedObj = await obj[0].update(data$)
          if(updatedObj) {
            return updatedObj
          }
          return false
        }
        return false
      } catch (err) {      
        console.error('CATCH', 'STORE', 'updateObject', err)
        return    
      }
    },

    reset({ commit }: any) {
      const userProps = {
        mapCenter: [55.755919, 37.617589] as [number, number],
        sessionStart: null
      }
      commit('RESET_USER_PROPS', userProps)
    }
  },

  getters: {
    isLogged(state: AuthState): boolean {
      return state.logged
    },
    
    getCurrentUser(state: AuthState): User | null {
      return state.user
    },
    
    getSavedUserProps(state: AuthState): AuthState['saved']['userProps'] {
      return state.saved.userProps
    },
    
    getSavedUserPosition(state: AuthState): any {
      const resp = state.saved.userProps.userPosition ? state.saved.userProps.userPosition.value : {}
      return resp
    },

    getEditMode(state: AuthState): boolean {
      return state.editMode
    },

    getAccess: (state: AuthState) => (level: number): boolean => {
      const access: Record<string, number> = {
        administrator: 0,
        manager: 1,
        operator: 2,
        inform_secur_user: 3
      }  
      if(!state.user?.role) return false
      const role = state.user.role
      return level >= access[role]
    }
  },
}